import React, {useMemo} from 'react';

import {SetupSectionProps, getStateViewDefaults, setupWizardApi} from 'src/lib/api/setupWizard';
import {buttonPrimary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import {PrerequisiteWarning} from './SetupStepSection';
import {useSetupMutation} from './useSetupMutation';

export default function RunDelineationSection({
  orgId,
  projectId,
  payload,
  queryKey,
  prerequisiteWarning,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: mutationError,
    lastCliCommand,
  } = useSetupMutation(queryKey);
  const [maxSubbasinArea, setMaxSubbasinArea] = React.useState(view.maxSubbasinArea);
  const [skipGaugeFinder, setSkipGaugeFinder] = React.useState(view.skipGaugeFinder);

  React.useEffect(() => {
    setMaxSubbasinArea(view.maxSubbasinArea);
    setSkipGaugeFinder(view.skipGaugeFinder);
  }, [view.maxSubbasinArea, view.skipGaugeFinder]);

  const handleRunDelineation = async () => {
    await mutate('run-delineation', async () => {
      await setupWizardApi.updateSettings(orgId, projectId, {
        maxSubbasinArea,
        skipGaugeFinder,
      });
      return await setupWizardApi.runDelineation(orgId, projectId);
    });
  };

  return (
    <div className="space-y-6">
      {prerequisiteWarning && <PrerequisiteWarning message={prerequisiteWarning} />}

      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Subbasin Area (km²)
          </label>
          <input
            type="number"
            className="block rounded-md border-gray-300 shadow-sm text-sm w-28"
            value={maxSubbasinArea}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMaxSubbasinArea(Number(e.target.value))
            }
          />
        </div>
        <div className="flex items-center gap-2 self-end mb-1">
          <input
            type="checkbox"
            id="skip-gauge-finder"
            checked={skipGaugeFinder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSkipGaugeFinder(e.target.checked)
            }
          />
          <label htmlFor="skip-gauge-finder" className="text-sm text-gray-700">
            Skip Gauge Finder
          </label>
        </div>
      </div>

      {/* Custom delineation shapefile upload */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Custom Delineation Shapefile (optional — .gpkg, .kml, or .zip)
        </label>
        {view.hasComparisonBasin ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-green-700 font-medium">Shapefile uploaded</span>
            <button
              type="button"
              disabled={isMutating}
              className="text-sm text-red-600 hover:text-red-800 underline"
              onClick={() =>
                mutate('delete-shapefile', () =>
                  setupWizardApi.deleteCustomShapefile(orgId, projectId)
                )
              }
            >
              {mutatingAction === 'delete-shapefile' ? 'Clearing…' : 'Clear'}
            </button>
          </div>
        ) : (
          <div>
            <input
              type="file"
              accept=".kml,.zip,.gpkg"
              disabled={isMutating}
              className="block text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (file) {
                  mutate('upload-shapefile', () =>
                    setupWizardApi.uploadCustomShapefile(orgId, projectId, file)
                  );
                  e.target.value = '';
                }
              }}
            />
            <p className="mt-1 text-xs text-gray-500">
              For .shp files: compress .prj, .shx, .dbf, and .shp into a .zip
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={isMutating}
          className={buttonPrimary}
          onClick={handleRunDelineation}
        >
          {mutatingAction === 'run-delineation' ? 'Running…' : 'Run Delineation'}
        </button>
        {mutatingAction === 'run-delineation' && (
          <span className="text-sm text-gray-500">This may take up to 5 minutes…</span>
        )}
        <ApiErrorMessage message={mutationError} />
      </div>
      <CliCommandBlock command={lastCliCommand} />

      <div className="border-t border-gray-200 pt-4 mt-2">
        <p className="text-xs text-gray-500">
          For OPG delineation (requires catchment shapefile + POI CSV), use the CLI:
        </p>
        <pre className="mt-1 text-xs text-gray-600 bg-gray-50 rounded p-2 overflow-x-auto">
          {`python scripts/setup_wizard_cli.py run-opg-delineation \\
  --org-id ${orgId} --project-id ${projectId} \\
  --shapefile catchments.gpkg --poi-csv poi_table.csv`}
        </pre>
      </div>
    </div>
  );
}
